import { useState, useEffect, useCallback } from "react";
import { AdminCard, AdminCardHead } from "components/admin/ui";
import { adminGet, adminPatch, adminDelete } from "lib/adminClient";

const fmt = (d) =>
  new Date(d).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

export default function MessagesPage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all"); // all | unread

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setList(await adminGet("/admin/contact"));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (m) => {
    try {
      const updated = await adminPatch(`/admin/contact/${m.id}`, { handled: !m.handled });
      setList((l) => l.map((x) => (x.id === m.id ? updated : x)));
    } catch (e) {
      setError(e.message);
    }
  };

  const del = async (id) => {
    try {
      await adminDelete(`/admin/contact/${id}`);
      setList((l) => l.filter((x) => x.id !== id));
    } catch (e) {
      setError(e.message);
    }
  };

  const shown = filter === "unread" ? list.filter((m) => !m.handled) : list;
  const unread = list.filter((m) => !m.handled).length;

  return (
    <div className="animate-fadeIn">
      <div className="flex gap-2 mb-5">
        {[
          ["all", `All (${list.length})`],
          ["unread", `Unread (${unread})`],
        ].map(([k, label]) => {
          const on = filter === k;
          return (
            <button
              key={k}
              onClick={() => setFilter(k)}
              className="px-[18px] py-2 rounded-full border-[1.5px] font-sans font-semibold text-[13px] cursor-pointer transition-colors"
              style={{
                borderColor: on ? "#005C40" : "#e6e9ec",
                background: on ? "#005C40" : "#fff",
                color: on ? "#fff" : "#5c5c5c",
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {error && (
        <div className="text-[13px] text-red bg-red/[0.08] rounded-lg px-4 py-2.5 mb-4">{error}</div>
      )}

      <AdminCard>
        <AdminCardHead title="Contact Messages" />
        {loading ? (
          <div className="px-[22px] py-8 text-center text-muted text-sm">Loading…</div>
        ) : shown.length === 0 ? (
          <div className="px-[22px] py-8 text-center text-muted text-sm">No messages.</div>
        ) : (
          shown.map((m, i) => (
            <div
              key={m.id}
              className="px-[22px] py-4"
              style={{
                borderBottom: i < shown.length - 1 ? "1px solid #f2f4f6" : "none",
                background: m.handled ? "transparent" : "#fffdf5",
              }}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-green-dark text-sm">{m.name}</span>
                    {!m.handled && (
                      <span className="text-[10px] font-bold text-[#9B7200] bg-[#fef6e0] px-2 py-0.5 rounded-full">
                        NEW
                      </span>
                    )}
                    <span className="text-[11.5px] text-[#9aa]">{fmt(m.createdAt)}</span>
                  </div>
                  <div className="text-[12.5px] text-muted mt-0.5">
                    {m.email || "no email"}
                    {m.phone ? ` · ${m.phone}` : ""}
                    {m.subject ? ` · ${m.subject}` : ""}
                  </div>
                  <p className="text-sm text-ink mt-2 whitespace-pre-wrap">{m.message}</p>
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  <button
                    onClick={() => toggle(m)}
                    className="border border-line bg-white rounded-md px-2.5 py-1.5 cursor-pointer text-xs font-semibold text-green-dark hover:bg-green-light"
                  >
                    {m.handled ? "Mark unread" : "Mark read"}
                  </button>
                  {m.email && (
                    <a
                      href={`mailto:${m.email}${m.subject ? `?subject=Re: ${encodeURIComponent(m.subject)}` : ""}`}
                      className="text-center border border-line bg-white rounded-md px-2.5 py-1.5 text-xs font-semibold text-green no-underline hover:bg-green-light"
                    >
                      Reply
                    </a>
                  )}
                  <button
                    onClick={() => del(m.id)}
                    className="bg-[#fdeaea] border-none rounded-md px-2.5 py-1.5 cursor-pointer text-xs text-[#c44]"
                  >
                    🗑 Delete
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </AdminCard>
    </div>
  );
}
